import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { OutgoingCallRequest } from "./outgoingCall";

// Как страница начинает исходящий звонок: useOutgoingCall().open({ … }).
//
// Сам звонок — софтфон и окно — живёт в OutgoingCallHost на уровне шелла,
// поэтому переживает смену чата, уход со страницы и закрытие карточки. Хост
// стоит рядом со страницами (как InboundCallHost), а не над ними, так что
// связь с ним — не контекст React, а реестр на уровне модуля, как счётчик
// активных звонков в softphone.ts: хост записывается сюда при монтировании.
// Хоста нет (страница вне шелла) — open() ничего не делает.

export interface OutgoingCallController {
  open(req: OutgoingCallRequest, opener: number): void;
  /** Открывший размонтирован: его onClosed звать больше нельзя. */
  release(opener: number): void;
}

let controller: OutgoingCallController | null = null;
const hostListeners = new Set<() => void>();

const hostChanged = () => hostListeners.forEach((l) => l());

/** Хост записывается при монтировании; возвращает отписку. */
export function registerOutgoingCallHost(c: OutgoingCallController): () => void {
  controller = c;
  hostChanged();
  return () => {
    // Шелл сменился (полная версия ↔ мобильная): новый хост уже записан.
    if (controller === c) {
      controller = null;
      hostChanged();
    }
  };
}

/** Есть ли хост, который примет звонок. В окне программы (/tray/app) хоста
 *  нет — там звонки живут в окошке у трея, — и кнопки «Позвонить» без него
 *  ничего бы не делали; страницы прячут их по этому признаку. */
export function useOutgoingCallAvailable(): boolean {
  return useSyncExternalStore(subscribeHost, () => controller !== null, () => false);
}

function subscribeHost(l: () => void) {
  hostListeners.add(l);
  return () => {
    hostListeners.delete(l);
  };
}

let openers = 0;

export function useOutgoingCall(): { open: (req: OutgoingCallRequest) => void } {
  // У каждого экземпляра хука свой номер: по нему хост знает, чьё окно, и
  // забывает onClosed, когда страница (лента, карточка) размонтирована, —
  // звонок при этом продолжается.
  const [opener] = useState(() => ++openers);
  useEffect(() => () => controller?.release(opener), [opener]);
  return useMemo(() => ({ open: (req: OutgoingCallRequest) => controller?.open(req, opener) }), [opener]);
}

// ---- слой после звонка ----
//
// Окно, которое страница открывает следом за звонком (вопрос про обратную
// связь после разговора с ЛПР), встаёт порталом в слой хоста — туда же, где
// стояло окно звонка. В самой ленте ему не место: пока шёл звонок, под окном
// могли открыть карточку кандидата (ссылка в нём самом), а лента — под
// карточкой, и слой у неё свой (на телефоне у `.conv` z-index, в полной версии
// она контейнер). Вопрос остался бы под карточкой невидимым, а на телефоне
// «Назад» закрыл бы его, не показав.
// Реестр — как у места плашки в стопке звонков (inboundCall/callStackSlot).

let afterCallLayer: HTMLElement | null = null;
const layerListeners = new Set<() => void>();

/** ref-колбэк слоя в OutgoingCallHost. */
export function setAfterCallLayer(el: HTMLElement | null) {
  if (afterCallLayer === el) return;
  afterCallLayer = el;
  layerListeners.forEach((l) => l());
}

function subscribeLayer(l: () => void) {
  layerListeners.add(l);
  return () => {
    layerListeners.delete(l);
  };
}

/** Слой хоста; null — хоста нет (страница вне шелла), окно остаётся на месте. */
export function useAfterCallLayer(): HTMLElement | null {
  return useSyncExternalStore(subscribeLayer, () => afterCallLayer, () => null);
}
